import React from 'react';
import FontAwesomeIcon from '../Icon';
import './attachment-list.scss';

interface AttachmentListProps {
  attachments: any[];
  onRemove?: (item: any) => void;
  readOnly?: boolean;
  clickable?: boolean;
}

interface DisplayAttachment {
  item: any;
  name: string;
  previewSrc: string;
  isImage: boolean;
  isPdf: boolean;
}

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
const MAX_FILENAME_LENGTH = 40;

const getExtension = (value: string): string =>
  value.split('?')[0].split('.').pop()?.toLowerCase() ?? '';

const truncateName = (value: string): string => {
  const decoded = decodeURIComponent(value);

  if (decoded.length <= MAX_FILENAME_LENGTH) {
    return decoded;
  }

  return `${decoded.slice(0, MAX_FILENAME_LENGTH)}…`;
};

// legacy/workaround: announcement attachments are a mix of saved records: ({ id, path }) and freshly-picked File objects ({ name, type, preview }).
const isVisible = (item: any): boolean => !(item.id && item.isDeleted === true);

const toDisplayAttachment = (item: any): DisplayAttachment => {
  const rawName: string = item.name || item.path || '';
  const previewSrc: string = item.preview || item.path || '';
  const extension: string = getExtension(rawName || previewSrc);

  const isImage: boolean =
    (typeof item.type === 'string' && item.type.startsWith('image/')) ||
    IMAGE_EXTENSIONS.includes(extension);

  const isPdf: boolean = item.type === 'application/pdf' || extension === 'pdf';

  return { item, name: truncateName(rawName), previewSrc, isImage, isPdf };
};

const renderThumbnail = (display: DisplayAttachment) => {
  if (display.isImage && display.previewSrc) {
    return <img src={display.previewSrc} alt={display.name} />;
  }

  if (display.isPdf) {
    return <FontAwesomeIcon glyph="file-pdf" weight="regular" size="small" />;
  }

  return <FontAwesomeIcon glyph="paperclip" weight="light" size="small" />;
};

export const AttachmentList = ({
  attachments,
  onRemove,
  readOnly = false,
  clickable = false,
}: AttachmentListProps) => {
  const displayItems: DisplayAttachment[] = attachments
    .filter((item: any) => isVisible(item))
    .map((item: any) => toDisplayAttachment(item));

  if (displayItems.length === 0) {
    return null;
  }

  const showRemove: boolean = !readOnly && Boolean(onRemove);

  return (
    <div className="attachment-list">
      {displayItems.map((display: DisplayAttachment, index: number) => {
        const main = (
          <>
            <div className="attachment-thumb">{renderThumbnail(display)}</div>
            <div className="attachment-name">{display.name}</div>
          </>
        );

        return (
          <div className="attachment-row" key={index}>
            {clickable && display.previewSrc ? (
              <a
                className="attachment-main"
                href={display.previewSrc}
                target="_blank"
                rel="noopener noreferrer"
              >
                {main}
              </a>
            ) : (
              <div className="attachment-main">{main}</div>
            )}

            {showRemove && (
              <button
                type="button"
                className="attachment-remove"
                onClick={() => onRemove?.(display.item)}
                aria-label="Remove attachment"
              >
                <FontAwesomeIcon
                  glyph="times-circle"
                  weight="light"
                  size="small"
                />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AttachmentList;
